import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '@HoudiniUI/Collapsible';
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { ChevronDown } from '../../../../../web/components/svgs/icons/ChevronDown';
import { shadowSm } from '../../../../../web/styles/shadows';
import { THEME } from '../../../../../web/components-v2/Theme/theme';

interface SettingCardProps {
	title: string;
	infoMessage?: string;
	defaultOpen?: boolean;
	children: React.ReactNode;
}

export function SettingCard({ title, infoMessage, defaultOpen = true, children }: SettingCardProps) {
	return (
		<Collapsible defaultOpen={defaultOpen} className={css(s.card)}>
			<CollapsibleTrigger className={css(s.header)}>
				<span className={css(s.titleRow)}>
					<span className={css(s.title)}>{title}</span>
					{infoMessage ? <InfoPopover message={infoMessage} /> : null}
				</span>
				<ChevronDown className={css(s.chevron)} />
			</CollapsibleTrigger>
			<CollapsiblePanel styles={[s.panel]}>{children}</CollapsiblePanel>
		</Collapsible>
	);
}

const s = StyleSheet.create({
	card: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowSm,
	},
	header: {
		boxSizing: 'border-box',
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.sm,
		padding: `14px ${THEME.spacing.md}px`, // 14px vertical per 1c card-header spec
		border: 'none',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		background: 'transparent',
		cursor: 'pointer',
		textAlign: 'left',
		':is([data-panel-open]) svg': {
			transform: 'rotate(180deg)',
		},
	},
	titleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
	},
	title: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontWeight: THEME.typography.fontWeights.bold,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
	},
	chevron: {
		flexShrink: 0,
		width: 16,
		height: 16,
		color: THEME.colors.icon.gray,
		transition: 'transform 150ms ease-out',
	},
	panel: {
		padding: THEME.spacing.md,
		display: 'flex',
		flexDirection: 'column',
		gap: 14, // 14px field gap per 1c spec
	},
});
